"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProxyClient = exports.retry = void 0;
const dht_1 = require("@streamr/dht");
const utils_1 = require("@streamr/utils");
const eventemitter3_1 = require("eventemitter3");
const sampleSize_1 = __importDefault(require("lodash/sampleSize"));
const NetworkRpc_1 = require("../../../generated/packages/trackerless-network/protos/NetworkRpc");
const NetworkRpc_client_1 = require("../../../generated/packages/trackerless-network/protos/NetworkRpc.client");
const ContentDeliveryRpcLocal_1 = require("../ContentDeliveryRpcLocal");
const ContentDeliveryRpcRemote_1 = require("../ContentDeliveryRpcRemote");
const NodeList_1 = require("../NodeList");
const formStreamPartDeliveryServiceId_1 = require("../formStreamPartDeliveryServiceId");
const Propagation_1 = require("../propagation/Propagation");
const utils_2 = require("../utils");
const ProxyConnectionRpcRemote_1 = require("./ProxyConnectionRpcRemote");
// TODO use options option or named constant?
const retry = async (task, description, abortSignal, delay = 10000) => {
    while (true) {
        try {
            const result = await task();
            return result;
        }
        catch {
            logger.warn(`Failed ${description} (retrying after delay)`, {
                delayInMs: delay
            });
        }
        await (0, utils_1.wait)(delay, abortSignal);
    }
};
exports.retry = retry;
const logger = new utils_1.Logger(module);
const SERVICE_ID = 'system/proxy-client';
class ProxyClient extends eventemitter3_1.EventEmitter {
    rpcCommunicator;
    contentDeliveryRpcLocal;
    options;
    duplicateDetectors = new Map();
    definition;
    connections = new Map();
    propagation;
    neighbors;
    abortController;
    constructor(options) {
        super();
        this.options = options;
        this.rpcCommunicator = new dht_1.ListeningRpcCommunicator((0, formStreamPartDeliveryServiceId_1.formStreamPartContentDeliveryServiceId)(options.streamPartId), options.transport);
        // TODO use options option or named constant?
        this.neighbors = new NodeList_1.NodeList((0, dht_1.toNodeId)(this.options.localPeerDescriptor), 1000);
        this.contentDeliveryRpcLocal = new ContentDeliveryRpcLocal_1.ContentDeliveryRpcLocal({
            localPeerDescriptor: this.options.localPeerDescriptor,
            streamPartId: this.options.streamPartId,
            markAndCheckDuplicate: (msg, prev) => (0, utils_2.markAndCheckDuplicate)(this.duplicateDetectors, msg, prev),
            broadcast: (message, previousNode) => this.broadcast(message, previousNode),
            onLeaveNotice: (remoteNodeId) => {
                const contact = this.neighbors.get(remoteNodeId);
                if (contact) {
                    // TODO should we catch possible promise rejection?
                    setImmediate(() => this.onNodeDisconnected(contact.getPeerDescriptor()));
                }
            },
            rpcCommunicator: this.rpcCommunicator,
            markForInspection: () => { }
        });
        this.propagation = new Propagation_1.Propagation({
            minPropagationTargets: options.minPropagationTargets,
            maxMessages: options.maxPropagationBufferSize,
            ttl: options.propagationBufferTtl,
            sendToNeighbor: async (neighborId, msg) => {
                const remote = this.neighbors.get(neighborId);
                if (remote) {
                    await remote.sendStreamMessage(msg);
                }
                else {
                    throw new Error('Propagation target not found');
                }
            }
        });
        this.abortController = new AbortController();
    }
    registerDefaultServerMethods() {
        this.rpcCommunicator.registerRpcNotification(NetworkRpc_1.StreamMessage, 'sendStreamMessage', (msg, context) => this.contentDeliveryRpcLocal.sendStreamMessage(msg, context));
        this.rpcCommunicator.registerRpcNotification(NetworkRpc_1.LeaveStreamPartNotice, 'leaveStreamPartNotice', (req, context) => this.contentDeliveryRpcLocal.leaveStreamPartNotice(req, context));
    }
    async setProxies(nodes, direction, userId, connectionCount) {
        logger.trace('Setting proxies', { streamPartId: this.options.streamPartId, peerDescriptors: nodes, direction, userId, connectionCount });
        if (connectionCount !== undefined && connectionCount > nodes.length) {
            throw new Error('Cannot set connectionCount above the size of the configured array of nodes');
        }
        const nodesIds = new Map();
        nodes.forEach((peerDescriptor) => {
            nodesIds.set((0, dht_1.toNodeId)(peerDescriptor), peerDescriptor);
        });
        this.definition = {
            nodes: nodesIds,
            userId,
            direction,
            connectionCount: connectionCount ?? nodes.length
        };
        await this.updateConnections();
    }
    async updateConnections() {
        await Promise.all(this.getInvalidConnections().map(async (id) => {
            await this.closeConnection(id);
        }));
        const connectionCountDiff = this.definition.connectionCount - this.connections.size;
        if (connectionCountDiff > 0) {
            await this.openRandomConnections(connectionCountDiff);
        }
        else if (connectionCountDiff < 0) {
            await this.closeRandomConnections(-connectionCountDiff);
        }
    }
    getInvalidConnections() {
        return Array.from(this.connections.keys()).filter((id) => {
            return !this.definition.nodes.has(id)
                || this.definition.direction !== this.connections.get(id).direction;
        });
    }
    async openRandomConnections(connectionCount) {
        const proxiesToAttempt = (0, sampleSize_1.default)(Array.from(this.definition.nodes.keys()).filter((id) => !this.connections.has(id)), connectionCount);
        await Promise.all(proxiesToAttempt.map((id) => this.attemptConnection(id, this.definition.direction, this.definition.userId)));
    }
    async attemptConnection(nodeId, direction, userId) {
        const peerDescriptor = this.definition.nodes.get(nodeId);
        const rpcRemote = new ProxyConnectionRpcRemote_1.ProxyConnectionRpcRemote(this.options.localPeerDescriptor, peerDescriptor, this.rpcCommunicator, NetworkRpc_client_1.ProxyConnectionRpcClient);
        const accepted = await rpcRemote.requestConnection(direction, userId);
        if (accepted) {
            this.options.connectionLocker.lockConnection(peerDescriptor, SERVICE_ID);
            this.connections.set(nodeId, { peerDescriptor, direction });
            const remote = new ContentDeliveryRpcRemote_1.ContentDeliveryRpcRemote(this.options.localPeerDescriptor, peerDescriptor, this.rpcCommunicator, NetworkRpc_client_1.ContentDeliveryRpcClient);
            this.neighbors.add(remote);
            this.propagation.onNeighborJoined(nodeId);
            logger.info('Open proxy connection', {
                nodeId,
                streamPartId: this.options.streamPartId
            });
        }
        else {
            logger.warn('Unable to open proxy connection', {
                nodeId,
                streamPartId: this.options.streamPartId
            });
        }
    }
    async closeRandomConnections(connectionCount) {
        const proxiesToDisconnect = (0, sampleSize_1.default)(Array.from(this.connections.keys()), connectionCount);
        await Promise.allSettled(proxiesToDisconnect.map((node) => this.closeConnection(node)));
    }
    async closeConnection(nodeId) {
        if (this.connections.has(nodeId)) {
            logger.info('Close proxy connection', {
                nodeId
            });
            const server = this.neighbors.get(nodeId);
            server?.leaveStreamPartNotice(this.options.streamPartId, false);
            this.removeConnection(this.connections.get(nodeId).peerDescriptor);
        }
    }
    removeConnection(peerDescriptor) {
        const nodeId = (0, dht_1.toNodeId)(peerDescriptor);
        this.connections.delete(nodeId);
        this.neighbors.remove(nodeId);
        this.options.connectionLocker.unlockConnection(peerDescriptor, SERVICE_ID);
    }
    broadcast(msg, previousNode) {
        if (!previousNode) {
            (0, utils_2.markAndCheckDuplicate)(this.duplicateDetectors, msg.messageId, msg.previousMessageRef);
        }
        this.emit('message', msg);
        this.propagation.feedUnseenMessage(msg, this.neighbors.getIds(), previousNode ?? null);
    }
    hasConnection(nodeId, direction) {
        return this.connections.has(nodeId) && this.connections.get(nodeId).direction === direction;
    }
    getDirection() {
        return this.definition.direction;
    }
    async onNodeDisconnected(peerDescriptor) {
        const nodeId = (0, dht_1.toNodeId)(peerDescriptor);
        if (this.connections.has(nodeId)) {
            this.options.connectionLocker.unlockConnection(peerDescriptor, SERVICE_ID);
            this.removeConnection(peerDescriptor);
            await (0, exports.retry)(() => this.updateConnections(), 'updating proxy connections', this.abortController.signal);
        }
    }
    async start() {
        this.registerDefaultServerMethods();
        (0, utils_1.addManagedEventListener)(this.options.transport, 'disconnected', 
        // TODO should we catch possible promise rejection?
        (peerDescriptor) => this.onNodeDisconnected(peerDescriptor), this.abortController.signal);
    }
    getDiagnosticInfo() {
        return {
            neighbors: this.neighbors.getAll().map((neighbor) => neighbor.getPeerDescriptor()),
        };
    }
    stop() {
        this.neighbors.getAll().forEach((remote) => {
            this.options.connectionLocker.unlockConnection(remote.getPeerDescriptor(), SERVICE_ID);
            remote.leaveStreamPartNotice(this.options.streamPartId, false);
        });
        this.neighbors.stop();
        this.rpcCommunicator.destroy();
        this.connections.clear();
        this.abortController.abort();
    }
}
exports.ProxyClient = ProxyClient;
//# sourceMappingURL=ProxyClient.js.map